import React, { useMemo, useState } from 'react';
import {
    Modal,
    Backdrop,
    Fade,
    Typography,
    Button,
    CircularProgress,
    TextField,
    IconButton,
    InputAdornment,
    Popper,
    Paper,
    Box,
    ClickAwayListener,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SimpleTableViewer from '../SimpleTable';
import ExternalFilter from './ExternalFilter';
import useLinkSpendModalStyles from './LinkSpendModal.style';

/** Max options rendered per filter field to keep dropdown UI responsive. */
const MAX_FILTER_OPTIONS = 1000;

/**
 * Maps raw filter API values into ExternalFilter option objects,
 * capping large arrays so the UI does not lag.
 */
const mapFilterOptions = (rawOptions = []) => {
    const mapped = [];
    const seen = new Set();

    for (let index = 0; index < rawOptions.length; index += 1) {
        if (mapped.length >= MAX_FILTER_OPTIONS) {
            break;
        }
        const option = rawOptions[index];
        const isNumeric = typeof option === 'number'
            || (option != null && option !== '' && !isNaN(Number(option)) && typeof option !== 'boolean');
        const numVal = typeof option === 'number' ? option : Number(option);
        const nextOption = {
            label: isNumeric ? numVal : (option ?? ''),
            value: isNumeric ? numVal : String(option ?? ''),
        };
        const seenKey = String(nextOption.value);
        if (seen.has(seenKey)) {
            continue;
        }
        seen.add(seenKey);
        mapped.push(nextOption);
    }

    return mapped;
};

/**
 * Modal to select spend rows and link them to the current obligation.
 */
const LinkSpendModal = (props) => {
    const {
        open,
        onClose,
        onSave,
        title,
        tableData,
        selectedTableDataElement,
        selectedRows,
        onCheckboxChange,
        rowIdKey,
        isLoading,
        isSaving,
        isCheckboxEnabled,
        isEnableMultipleSelection,
        isEnableSelectAll,
        isSearchEnabled,
        filterConfig,
        filterData,
        page,
        rowsPerPage,
        onPageChange,
        onRowsPerPageChange,
        onApplyFilters,
        getSpendFilters,
        dataModelId,
        appId,
        filterDataUniqueId,
        formData,
    } = props;
    const classes = useLinkSpendModalStyles();
    const [searchText, setSearchText] = useState('');
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [localSelectedFilterData, setLocalSelectedFilterData] = useState(null);
    const [savedFilterData, setSavedFilterData] = useState(null);
    const [filterButtonText, setFilterButtonText] = useState('Filter by');

    const rows = Array.isArray(tableData?.data) ? tableData.data : [];
    const visibleRows = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase();
        if (!normalizedSearch) {
            return rows;
        }
        const searchableFields = (selectedTableDataElement || []).map(column => column.value).filter(Boolean);
        return rows.filter(row => searchableFields.some(field => (
            String(row?.[field] ?? '').toLowerCase().includes(normalizedSearch)
        )));
    }, [rows, searchText, selectedTableDataElement]);

    const externalFilters = useMemo(() => (
        (filterConfig || []).map(item => ({
            value: item.value,
            label: item.label,
            type: item.type === 'date' || item.isDate ? 'date' : 'select',
        }))
    ), [filterConfig]);

    const externalFilterData = useMemo(() => (
        (filterConfig || []).map((item) => mapFilterOptions(filterData?.[item.value] || []))
    ), [filterConfig, filterData]);

    const selectedFiltersCount = useMemo(() => {
        const filterValues = localSelectedFilterData !== null ? localSelectedFilterData : {};
        let totalCount = 0;
        Object.values(filterValues).forEach((value) => {
            if (value && Array.isArray(value) && value.length > 0) {
                totalCount += value.length;
            } else if (value && typeof value === 'object' && (value.startDate || value.endDate)) {
                totalCount += 1;
            }
        });
        return totalCount;
    }, [localSelectedFilterData]);

    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
        setFilterButtonText(selectedFiltersCount > 0 ? `Filter added (${selectedFiltersCount})` : 'Filter by');
    };

    const handleFilterClose = (action) => {
        if (action === 'save') {
            setSavedFilterData(localSelectedFilterData);
            setFilterButtonText(
                selectedFiltersCount > 0 ? `Filter added (${selectedFiltersCount})` : 'Filter by'
            );
            if (onApplyFilters) {
                onApplyFilters(localSelectedFilterData || {});
            }
        } else if (action === 'clear') {
            const clearedFilters = {};
            (filterConfig || []).forEach((item) => {
                clearedFilters[item.value] = null;
            });
            setLocalSelectedFilterData(clearedFilters);
            setSavedFilterData(null);
            setFilterButtonText('Filter by');
            if (onApplyFilters) {
                onApplyFilters({});
            }
        } else if (action === 'close') {
            setLocalSelectedFilterData(savedFilterData || null);
        }
        setFilterAnchorEl(null);
    };

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                onClose();
            }}
            className={classes.modal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
            disableEscapeKeyDown
        >
            <Fade in={open}>
                <div className={classes.paper}>
                    <div className={classes.header}>
                        <div>
                            <Typography className={classes.title}>{title || 'Link New Spend'}</Typography>
                            <Typography className={classes.subtitle}>
                                Find and select spend records to link to this opportunity.
                            </Typography>
                        </div>
                        <IconButton onClick={onClose} disabled={isSaving} aria-label="Close link spend modal">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>

                    <div className={classes.toolbar}>
                        {isSearchEnabled && (
                            <TextField
                                className={classes.search}
                                variant="outlined"
                                size="small"
                                placeholder="Search visible records"
                                value={searchText}
                                onChange={event => setSearchText(event.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ fontSize: 18, color: '#78798E' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                        {filterConfig?.length > 0 && (
                            <Button
                                className={classes.filterButton}
                                variant="contained"
                                endIcon={<ArrowDropDownIcon />}
                                onClick={handleFilterClick}
                            >
                                {filterButtonText}
                            </Button>
                        )}
                        <Typography className={classes.selectionText}>
                            {selectedRows?.length || 0} selected
                        </Typography>
                    </div>

                    <div className={classes.content}>
                        {isLoading ? (
                            <div className={classes.loading}>
                                <CircularProgress size={28} />
                            </div>
                        ) : (
                            <SimpleTableViewer
                                {...props}
                                title=""
                                dataObj={visibleRows}
                                selectedDataElement={[]}
                                selectedTableDataElement={selectedTableDataElement || []}
                                isActionEnabled={false}
                                isObjectTable={false}
                                pagination={true}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={onPageChange}
                                onRowsPerPageChange={onRowsPerPageChange}
                                totalRecords={tableData?.totalRecords || (tableData?.data || []).length}
                                isCheckbox={Boolean(isCheckboxEnabled)}
                                isEnableMultipleSelection={isEnableMultipleSelection}
                                isEnableSelectAll={isEnableSelectAll}
                                onCheckboxChange={onCheckboxChange}
                                selectedRows={selectedRows}
                                rowIdKey={rowIdKey}
                                isDownloadButton={false}
                                isLinkButton={false}
                            />
                        )}
                    </div>

                    <div className={classes.footer}>
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.cancelButton}
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            className={classes.saveButton}
                            onClick={onSave}
                            disabled={isSaving || !selectedRows?.length}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>

                    <Popper
                        open={Boolean(filterAnchorEl)}
                        anchorEl={filterAnchorEl}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        style={{ zIndex: 2200 }}
                    >
                        <ClickAwayListener onClickAway={() => handleFilterClose('close')}>
                            <Paper className={classes.filterPopper}>
                                <Box style={{ padding: '20px' }}>
                                    <Box style={{ marginBottom: '20px' }}>
                                        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '16px' }}>
                                            Filter
                                        </Typography>
                                        <ExternalFilter
                                            externalFilters={externalFilters}
                                            externalFilterData={externalFilterData}
                                            externalFilterValues={localSelectedFilterData !== null ? localSelectedFilterData : {}}
                                            onExternalFilterChange={setLocalSelectedFilterData}
                                            setLocalSelectedFilterData={setLocalSelectedFilterData}
                                            localSelectedFilterData={localSelectedFilterData}
                                            align="vertical"
                                            isNewV1Theme={true}
                                            dataModelId={dataModelId}
                                            appId={appId}
                                            dataUniqueId={filterDataUniqueId}
                                            getModelDataDropDown={(payload) => {
                                                if (!getSpendFilters) {
                                                    return;
                                                }
                                                getSpendFilters({
                                                    appId: payload.appId,
                                                    dataModelId: payload.id,
                                                    dataUniqueId: payload.dataUniqueId,
                                                    payload: {
                                                        field: payload.field,
                                                    },
                                                });
                                            }}
                                            formData={formData}
                                            conditions={[]}
                                        />
                                    </Box>
                                    <Box className={classes.filterFooter}>
                                        {filterConfig?.length > 0 && (
                                            <Button
                                                onClick={() => handleFilterClose('clear')}
                                                style={{ color: '#1976d2', textTransform: 'capitalize' }}
                                            >
                                                Clear All
                                            </Button>
                                        )}
                                        <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                                            <Button
                                                onClick={() => handleFilterClose('close')}
                                                style={{
                                                    color: '#1976d2',
                                                    textTransform: 'capitalize',
                                                    height: 32,
                                                }}
                                            >
                                                Close
                                            </Button>
                                            <Button
                                                onClick={() => handleFilterClose('save')}
                                                variant="contained"
                                                style={{
                                                    backgroundColor: '#1976d2',
                                                    color: 'white',
                                                    textTransform: 'capitalize',
                                                    height: 32,
                                                }}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </Box>
                                </Box>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                </div>
            </Fade>
        </Modal>
    );
};

export default LinkSpendModal;
